
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import TopNavigation from '@/components/TopNavigation';
import BottomNavigation from '@/components/BottomNavigation';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import catFastFood from '@/assets/cat-fast-food.png';
import catCoffee from '@/assets/cat-coffee.png';
import catHealthy from '@/assets/cat-healthy.png';
import catDessert from '@/assets/cat-dessert.png';
import catFineDining from '@/assets/cat-fine-dining.png';
import catCasualDining from '@/assets/cat-casual-dining.png';
import catBakery from '@/assets/cat-bakery.png';
import catMarketplace from '@/assets/cat-marketplace.png';
import catRide from '@/assets/cat-ride.jpg';
import RestaurantCard from '@/components/RestaurantCard';
import { shuffleArray } from '@/utils/shuffleArray';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/providers/AuthProvider';
import { validateZoneForRestaurantAccess } from '@/utils/zoneValidation';
import { useRealtimeOrderNotifications } from '@/hooks/useRealtimeOrderNotifications';
import { getDistanceFromAddress } from '@/utils/gambianLocations';
import PromoBanner from '@/components/PromoBanner';
import StoresNearYou from '@/components/StoresNearYou';
import { FEATURE_FLAGS, isRideDriverEmail } from '@/config/features';

interface Restaurant {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  created_at: string;
  is_open: boolean;
  opening_hours: any;
  average_rating?: number;
  categories: string[];
  avg_prep_time_min?: number;
  avg_prep_time_max?: number;
  distance?: number;
}

const HomePage = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [locationAllowed, setLocationAllowed] = useState<boolean>(true);
  const [locationMessage, setLocationMessage] = useState<string>('');
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  // Initialize realtime push notifications for customers
  useRealtimeOrderNotifications();


  const isAdminUser = FEATURE_FLAGS.ADMIN_EMAILS.includes(user?.email || '');

  const categories = [
    { image: catFastFood, label: t('fast_food'), value: 'fast_food' },
    { image: catCoffee, label: t('coffee'), value: 'coffee' },
    { image: catHealthy, label: t('healthy'), value: 'healthy' },
    { image: catDessert, label: t('dessert'), value: 'dessert' },
    { image: catFineDining, label: t('fine_dining'), value: 'fine_dining' },
    { image: catCasualDining, label: t('casual_dining'), value: 'casual_dining' },
    ...(isAdminUser 
      ? [{ image: catRide, label: 'Ride', value: 'ride', isRide: true }] 
      : [{ image: catBakery, label: t('bakery'), value: 'bakery' }]),
    { image: catMarketplace, label: 'Marketplace', value: 'marketplace', isMarketplace: true },
  ];

  const getUserLocation = useCallback(async () => {
    if (user) {
      // For authenticated users, get from saved locations
      try {
        const { data, error } = await supabase
          .from('saved_locations')
          .select('latitude, longitude')
          .eq('user_id', user.id)
          .eq('is_default', true)
          .single();

        if (data && data.latitude && data.longitude) {
          setUserLocation({ latitude: data.latitude, longitude: data.longitude });
        }
      } catch (error) {
        console.error('Error getting user location:', error);
      }
    } else {
      // For unauthenticated users, try to get from localStorage
      const savedLocationStr = localStorage.getItem('selectedLocation');
      if (savedLocationStr) {
        try {
          const location = JSON.parse(savedLocationStr);
          if (location.latitude && location.longitude) {
            setUserLocation({ latitude: location.latitude, longitude: location.longitude });
          }
        } catch (error) {
          console.error('Error parsing saved location:', error);
        }
      }
    }
  }, [user]);

  const sortRestaurantsByDistance = useCallback((restaurantsList: Restaurant[]) => {
    if (!userLocation) return restaurantsList;

    return restaurantsList.map(restaurant => {
      const distanceData = getDistanceFromAddress(
        userLocation.latitude,
        userLocation.longitude,
        restaurant.address
      );
      return {
        ...restaurant,
        distance: distanceData.distance
      };
    }).sort((a, b) => (a.distance || 999) - (b.distance || 999));
  }, [userLocation]);

  const checkUserLocationAccess = useCallback(async () => {
    if (user) {
      // For authenticated users, check their default location
      try {
        const { data, error } = await supabase
          .from('saved_locations')
          .select('address_line, city')
          .eq('user_id', user.id)
          .eq('is_default', true)
          .single();

        if (data) {
          const validation = validateZoneForRestaurantAccess(`${data.address_line} ${data.city}`);
          setLocationAllowed(validation.isAllowed);
          if (!validation.isAllowed) {
            setLocationMessage(validation.message || '');
          }
        }
      } catch (error) {
        console.error('Error checking user location:', error);
      }
    } else {
      // For unauthenticated users, check localStorage
      const savedLocationStr = localStorage.getItem('selectedLocation');
      if (savedLocationStr) {
        try {
          const location = JSON.parse(savedLocationStr);
          const validation = validateZoneForRestaurantAccess(location.address);
          setLocationAllowed(validation.isAllowed);
          if (!validation.isAllowed) {
            setLocationMessage(validation.message || '');
          }
        } catch (error) {
          console.error('Error parsing saved location:', error);
        }
      }
    }
  }, [user]);

  const fetchRestaurants = useCallback(async () => {
    const abortController = new AbortController();
    
    try {
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('restaurants')
        .select('id, name, description, image_url, rating, address, created_at, is_open, opening_hours, categories, avg_prep_time_min, avg_prep_time_max')
        .eq('business_type', 'restaurant')
        .order('created_at', { ascending: false });

      if (restaurantError) throw restaurantError;

      if (abortController.signal.aborted) return;

      const restaurantIds = restaurantData?.map(r => r.id) || [];
      const { data: ratingsData, error: ratingsError } = await supabase
        .from('ratings')
        .select('restaurant_id, restaurant_rating')
        .in('restaurant_id', restaurantIds)
        .not('restaurant_rating', 'is', null);

      if (ratingsError) {
        console.error('Error fetching ratings:', ratingsError);
      }

      if (abortController.signal.aborted) return;

      const ratingsMap = new Map();
      if (ratingsData) {
        ratingsData.forEach(rating => {
          if (!ratingsMap.has(rating.restaurant_id)) {
            ratingsMap.set(rating.restaurant_id, []);
          }
          ratingsMap.get(rating.restaurant_id).push(rating.restaurant_rating);
        });
      }

      const restaurantsWithRatings = restaurantData?.map(restaurant => {
        const ratings = ratingsMap.get(restaurant.id) || [];
        const averageRating = ratings.length > 0 
          ? ratings.reduce((sum: number, rating: number) => sum + rating, 0) / ratings.length 
          : restaurant.rating;
        
        return {
          ...restaurant,
          average_rating: averageRating,
          created_at: restaurant.created_at || new Date().toISOString(),
          is_open: restaurant.is_open ?? true,
          opening_hours: restaurant.opening_hours || {},
          categories: restaurant.categories || [],
          avg_prep_time_min: restaurant.avg_prep_time_min || 25,
          avg_prep_time_max: restaurant.avg_prep_time_max || 35
        };
      }) || [];

      if (!abortController.signal.aborted) {
        setRestaurants(restaurantsWithRatings);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !abortController.signal.aborted) {
        console.error('Error fetching restaurants:', error);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const initializeData = async () => {
      await checkUserLocationAccess();
      await getUserLocation();
      cleanup = await fetchRestaurants();
    };

    initializeData();
    
    const channel = supabase
      .channel('restaurant-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'restaurants'
        },
        () => {
          fetchRestaurants();
        }
      )
      .subscribe();

    // Listen for location updates
    const handleLocationUpdate = () => {
      checkUserLocationAccess();
      getUserLocation();
    };

    window.addEventListener('storage', handleLocationUpdate);
    window.addEventListener('locationUpdated', handleLocationUpdate);

    return () => {
      if (cleanup) cleanup();
      supabase.removeChannel(channel);
      window.removeEventListener('storage', handleLocationUpdate);
      window.removeEventListener('locationUpdated', handleLocationUpdate);
    };
  }, [fetchRestaurants, checkUserLocationAccess, getUserLocation]);

  const isNewRestaurant = (createdAt: string) => {
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    return new Date(createdAt) > twoMonthsAgo;
  };

  const filteredRestaurants = selectedCategory 
    ? restaurants.filter(restaurant => restaurant.categories.includes(selectedCategory))
    : restaurants;

  // Stable shuffle: only re-shuffles when the underlying restaurant set actually
  // changes (id list), not on every render. Prevents the "glitchy" reshuffling
  // every time a child component re-renders or the user logs in.
  const filteredKey = useMemo(
    () => filteredRestaurants.map(r => r.id).sort().join(','),
    [filteredRestaurants]
  );

  const newRestaurants = useMemo(
    () => shuffleArray(filteredRestaurants.filter(r => isNewRestaurant(r.created_at))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredKey]
  );

  const topPickRestaurants = useMemo(
    () => shuffleArray([...filteredRestaurants]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredKey]
  );

  // Featured: locked to specific 5 restaurants (no shuffling, no glitching).
  const FEATURED_RESTAURANT_IDS = [
    '0d29453a-9312-405c-b581-954fe0d2368f', // Kadie Kadie
    '2874e157-906f-4254-ba53-b96d30034b03', // Bubbles - Tropic Shopping Center
    'd3c8ba52-9ec2-4df1-949a-62954efd34fd', // SUŃU CAFE
    'e32a4506-3001-4212-b1a1-4d04d12d8fcc', // Paradiso Pizzeria
    '842f5b62-fab3-4184-baf1-1e7fb4fffacb', // Ndey's Kitchen
  ];
  const featuredRestaurants = useMemo(() => {
    const byId = new Map(filteredRestaurants.map(r => [r.id, r]));
    return FEATURED_RESTAURANT_IDS
      .map(id => byId.get(id))
      .filter((r): r is Restaurant => Boolean(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredKey]);

  // Get restaurants with active deals (free delivery first)
  const [dealsRestaurants, setDealsRestaurants] = useState<Restaurant[]>([]);

  useEffect(() => {
    const fetchDealsRestaurants = async () => {
      const { data: promos } = await supabase
        .from('promos')
        .select('restaurant_id, discount_type')
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .lte('start_date', new Date().toISOString());

      // Kadi Kadi and PYFC Kitchen IDs for 10% off promotion
      const tenPercentOffIds = [
        '0d29453a-9312-405c-b581-954fe0d2368f', // Kadie Kadie
        'c625ff68-b35b-4548-9a34-c3a68265b2a0'  // PYFC Kitchen
      ];

      // Tropicana for 5% off promotion (delivery only)
      const tropicanaIds = [
        'c02c5e24-21ab-494b-a319-7bf46d8d224d'  // Tropicana ice cream and pastry
      ];

      // JKO BRUNCH for 1-week free delivery promo (DB-driven, kept here as a
      // safety net so it always shows in Deals for You).
      const jkoBrunchIds = ['e1961b99-490f-483a-9a87-5772017934c4'];

      if (promos) {
        const restaurantIds = [...new Set(promos.map(p => p.restaurant_id))];
        const allDealsIds = [...new Set([...restaurantIds, ...tenPercentOffIds, ...tropicanaIds, ...jkoBrunchIds])];
        const dealsRestaurantsList = filteredRestaurants.filter(r =>
          allDealsIds.includes(r.id)
        );

        // Sort by distance first, then prioritize free delivery
        const sorted = sortRestaurantsByDistance(dealsRestaurantsList).sort((a, b) => {
          const aHasFreeDelivery = promos.some(p => p.restaurant_id === a.id && p.discount_type === 'free_delivery')
            || jkoBrunchIds.includes(a.id);
          const bHasFreeDelivery = promos.some(p => p.restaurant_id === b.id && p.discount_type === 'free_delivery')
            || jkoBrunchIds.includes(b.id);

          if (aHasFreeDelivery && !bHasFreeDelivery) return -1;
          if (!aHasFreeDelivery && bHasFreeDelivery) return 1;
          return 0;
        });

        setDealsRestaurants(sorted);
      }
    };

    fetchDealsRestaurants();
    // Only refetch when the underlying restaurant set or location changes,
    // not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredKey, userLocation]);
  
  // Sort all restaurants by distance for the "All Restaurants" section
  const allRestaurantsByDistance = sortRestaurantsByDistance(filteredRestaurants);

  const EmptyState = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div className="text-center py-8 text-gray-500">
      <p className="font-medium">{title}</p>
      <p className="text-sm">{subtitle}</p>
    </div>
  );

  const RestaurantSection = ({ title, restaurants, emptyTitle, emptySubtitle, variant = 'horizontal', showFavorite = false }: {
    title: string;
    restaurants: Restaurant[];
    emptyTitle: string;
    emptySubtitle: string;
    variant?: 'horizontal' | 'vertical';
    showFavorite?: boolean;
  }) => (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      {loading ? (
        <div className={variant === 'vertical' ? "grid grid-cols-1 gap-4" : "flex gap-4 overflow-x-auto"}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={`${variant === 'vertical' ? 'w-full h-64' : 'flex-shrink-0 w-64 h-48'} bg-gray-200 rounded-lg animate-pulse`} />
          ))}
        </div>
      ) : restaurants.length > 0 ? (
        <div className={variant === 'vertical' ? "grid grid-cols-1 gap-4" : "flex gap-4 overflow-x-auto pb-2"}>
          {restaurants.map((restaurant) => (
            <RestaurantCard 
              key={restaurant.id} 
              restaurant={restaurant} 
              onClick={() => navigate(`/restaurant/${restaurant.id}`)}
              variant={variant}
              showFavorite={showFavorite}
            />
          ))}
        </div>
      ) : (
        <EmptyState title={emptyTitle} subtitle={emptySubtitle} />
      )}
    </div>
  );

  // If location is not allowed, show restriction message
  if (!locationAllowed) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <TopNavigation />
        
        <div className="px-4 py-6">
          <div className="bg-white rounded-lg p-8 text-center shadow-sm">
            <div className="text-6xl mb-4">📍</div>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">Service Not Available</h2>
            <p className="text-gray-600 mb-6">{locationMessage}</p>
            <Button 
              onClick={() => navigate('/location')}
              className="bg-red-600 hover:bg-red-700"
            >
              Update Location
            </Button>
          </div>
        </div>

        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <TopNavigation />
      
      <div className="px-4 py-6">
        {/* Categories */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {categories.map((category) => (
            <button
              key={category.label}
              className={`flex flex-col items-center gap-1.5 p-1.5 rounded-lg transition-colors ${
                selectedCategory === category.value ? 'bg-red-50 ring-2 ring-red-200' : ''
              }`}
              onClick={() => {
                if ((category as any).isRide) {
                  navigate('/ride');
                } else if ((category as any).isMarketplace) {
                  navigate('/grocery');
                } else {
                  setSelectedCategory(
                    selectedCategory === category.value ? null : category.value
                  );
                }
              }}
            >
              <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img src={category.image} alt={category.label} className="w-full h-full object-cover" />
              </div>
              <span className="text-xs font-medium text-foreground truncate w-full text-center">{category.label}</span>
            </button>
          ))}
        </div>

        {selectedCategory && (
          <div className="mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedCategory(null)}
            >
              {t('clear_filter')}
            </Button>
          </div>
        )}

        {/* Promo Banners */}
        {!selectedCategory && <PromoBanner type="home" />}

        {/* When category is selected, show only the filtered restaurants */}
        {selectedCategory ? (
          <RestaurantSection
            title={`${categories.find(c => c.value === selectedCategory)?.label} ${t('restaurants')}`}
            restaurants={filteredRestaurants}
            emptyTitle={t('no_restaurants_available')}
            emptySubtitle={t('check_back_later')}
            variant="vertical"
            showFavorite={true}
          />
        ) : (
          <>
            {/* Default horizontal layout when no category selected */}
            {/* Featured Restaurants */}
            <RestaurantSection
              title={t('featured_restaurants')}
              restaurants={featuredRestaurants}
              emptyTitle={t('no_restaurants_available')}
              emptySubtitle={t('check_back_later')}
            />

            {/* Deals for You */}
            <RestaurantSection
              title="Deals for You"
              restaurants={dealsRestaurants}
              emptyTitle="No deals available"
              emptySubtitle="Check back soon for special offers"
            />

            {/* New to You */}
            <RestaurantSection
              title={t('new_to_you')}
              restaurants={newRestaurants}
              emptyTitle={t('no_new_restaurants')}
              emptySubtitle={t('all_restaurants_old')}
            />

            {/* Stores Near You */}
            <StoresNearYou />

            {/* Top Picks */}
            <RestaurantSection
              title={t('top_picks')}
              restaurants={topPickRestaurants.slice(0, 10)}
              emptyTitle={t('no_top_picks')}
              emptySubtitle={t('finding_restaurants')}
            />

            {/* All Restaurants */}
            <RestaurantSection
              title={t('all_restaurants')}
              restaurants={allRestaurantsByDistance}
              emptyTitle={t('no_restaurants_available')}
              emptySubtitle={t('adding_restaurants')}
              variant="vertical"
              showFavorite={true}
            />
          </>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default HomePage;
